/**
 * The shell every authenticated screen uses.
 *
 * The sidebar carries the mahallu's identity and live counts rather than being
 * a bare list of links: staff should be able to tell at a glance which
 * community they are in and how big it is. Nav items a role cannot access are
 * hidden, not disabled -- a disabled item invites a support call.
 */
import {
  BanknoteArrowUp,
  BookMarked,
  Building2,
  Bus,
  Check,
  ChevronLeft,
  ChevronsUpDown,
  ClipboardList,
  Coins,
  GraduationCap,
  HandCoins,
  HardHat,
  HeartPulse,
  Home,
  Languages,
  LayoutDashboard,
  MapPin,
  Menu,
  Settings,
  TrendingUp,
  UserCog,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useSummary } from "@/features/community/api";
import { useSession, type Permission } from "@/lib/auth/session";
import { cn } from "@/lib/cn";
import { formatCount } from "@/lib/format";
import { setLocale, type Locale } from "@/lib/i18n";

/** "Ibrahim Musthafa" -> "IM". Falls back to a dash rather than an empty pill. */
function initials(name: string | undefined): string {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("") || "—";
}

interface NavItem {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  path: string;
  /**
   * What the screen behind this link needs.
   *
   * A permission, not a role list: the role lists here were maintained by
   * hand against the matrix in `app/core/permissions.py` and had drifted --
   * Areas, Houses, Families and People carried no list at all, so a VIEWER
   * (dashboards only) and a SURVEYOR (areas and houses only) were offered
   * screens that answered 403. An item with none is open to every signed-in
   * user; only Settings is.
   */
  needs?: Permission[];
  /**
   * Every one of these, where `needs` is any one. Registering a family writes
   * a house, a family and its people in one save, and a role short of any of
   * the three would fill in the whole form only to have it refused.
   */
  needsAll?: Permission[];
  count?: number | undefined;
}

// Registers, Madrasa and Finance appear only once the screen behind them
// exists -- a link to nothing is worse than an absent link.
function useNav(): Array<{ id: string; labelKey: string; items: NavItem[] }> {
  const { data } = useSummary();
  // One link per screen, grouped by the office's own work -- the way the
  // desktop package it used for years grouped its menus. Each job is reached
  // one way: "Register a family" is the Families page's main button, not a
  // second copy in the rail.
  return [
    {
      id: "overview",
      labelKey: "nav.overview",
      items: [
        {
          id: "dashboard",
          labelKey: "nav.mahalluDashboard",
          icon: LayoutDashboard,
          path: "/",
          needs: ["dashboard.read"],
        },
      ],
    },
    {
      id: "community",
      labelKey: "nav.community",
      items: [
        {
          id: "families",
          labelKey: "nav.families",
          icon: Building2,
          path: "/families",
          needs: ["family.read"],
          count: data?.families,
        },
        {
          id: "houses",
          labelKey: "nav.houses",
          icon: Home,
          path: "/houses",
          needs: ["house.read"],
          count: data?.houses,
        },
        {
          id: "people",
          labelKey: "nav.people",
          icon: Users,
          path: "/people",
          needs: ["person.read"],
          count: data?.people,
        },
        {
          id: "areas",
          labelKey: "nav.areas",
          icon: MapPin,
          path: "/areas",
          needs: ["area.read"],
        },
      ],
    },
    // Money that comes in from the community.
    {
      id: "collections",
      labelKey: "nav.collections",
      items: [
        {
          id: "maasavari",
          labelKey: "nav.maasavari",
          icon: BanknoteArrowUp,
          path: "/collect",
          needs: ["maasavari.read"],
        },
        {
          id: "jumua",
          labelKey: "nav.jumua",
          icon: Coins,
          path: "/jumua",
          needs: ["fund.general.read"],
        },
        {
          id: "donations",
          labelKey: "nav.donations",
          icon: HandCoins,
          path: "/donations",
          needs: ["fund.general.read"],
        },
      ],
    },
    {
      id: "records",
      labelKey: "nav.registers",
      items: [
        {
          id: "registers",
          labelKey: "nav.registersAndCertificates",
          icon: BookMarked,
          path: "/registers",
          needs: ["register.read"],
        },
        {
          id: "survey",
          labelKey: "nav.survey",
          icon: ClipboardList,
          path: "/survey",
          needs: ["survey.review"],
        },
      ],
    },
    {
      id: "madrasa",
      labelKey: "nav.madrasa",
      items: [
        {
          id: "madrasa",
          labelKey: "nav.madrasaStudents",
          icon: GraduationCap,
          path: "/madrasa",
          needs: ["madrasa.read"],
        },
        {
          id: "madrasa-fees",
          labelKey: "nav.madrasaFees",
          icon: Bus,
          path: "/madrasa-fees",
          needs: ["madrasa.fee.read"],
        },
      ],
    },
    // The two segregated funds, each with its own committee.
    {
      id: "welfare",
      labelKey: "nav.welfareFunds",
      items: [
        {
          id: "zakat",
          labelKey: "nav.zakat",
          icon: HandCoins,
          path: "/zakat",
          needs: ["zakat.read"],
        },
        {
          id: "palliative",
          labelKey: "nav.palliative",
          icon: HeartPulse,
          path: "/palliative",
          needs: ["palliative.read"],
        },
      ],
    },
    // Running the masjid: staff, upkeep, and how the money stands.
    {
      id: "office",
      labelKey: "nav.office",
      items: [
        {
          id: "staff",
          labelKey: "nav.staff",
          icon: UserCog,
          path: "/staff",
          needs: ["staff.read"],
        },
        {
          id: "maintenance",
          labelKey: "nav.maintenance",
          icon: HardHat,
          path: "/maintenance",
          needs: ["fund.general.read"],
        },
        {
          id: "revenue",
          labelKey: "dashboards:revenue.title",
          icon: TrendingUp,
          path: "/revenue",
          needs: ["fund.general.read"],
        },
      ],
    },
    // The mahallu's own details.
    {
      id: "admin",
      labelKey: "nav.admin",
      items: [
        {
          id: "settings",
          labelKey: "nav.settings",
          icon: Settings,
          path: "/settings",
        },
      ],
    },
  ];
}

/**
 * Is this nav item the screen we are on?
 *
 * The boundary matters. A plain `startsWith` lit up "Madrasa" whenever you
 * opened "Madrasa fees", because `/madrasa-fees` does start with `/madrasa` --
 * two items highlighted at once, and the sidebar claiming you were somewhere
 * you were not.
 *
 * A trailing slash is what separates a section from a longer name that merely
 * begins the same way: `/houses/018f...` is the Houses screen and must light
 * it up, `/madrasa-fees` is not the Madrasa screen and must not.
 *
 * `others` are the rest of the nav's paths. When one of them is a deeper,
 * better match -- "Register a family" at `/families/new` -- it takes the
 * highlight and "Families" gives it up, so exactly one item is lit.
 */
export function isCurrent(
  currentPath: string,
  itemPath: string,
  others: readonly string[] = [],
): boolean {
  const within = (path: string) =>
    path === "/"
      ? // The dashboard is also at /mahallu, the address it had before.
        currentPath === "/" || currentPath === "/mahallu"
      : currentPath === path || currentPath.startsWith(`${path}/`);
  if (!within(itemPath)) return false;
  return !others.some(
    (other) => other.length > itemPath.length && within(other),
  );
}

export function AppShell({
  children,
  currentPath,
  onNavigate,
}: {
  children: ReactNode;
  currentPath: string;
  onNavigate: (path: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { session, switchMahallu, can } = useSession();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // Collapsed is session-local, not persisted: the rail is identity as much
  // as navigation, and a stale "collapsed" surprise on next login costs more
  // than re-clicking the toggle.
  const [collapsed, setCollapsed] = useState(false);
  // Below `lg` the rail is an off-canvas drawer rather than a column. It was
  // simply `hidden` before, which left every phone and tablet with no way to
  // reach another screen at all.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = useNav();
  const allPaths = nav.flatMap((group) => group.items.map((item) => item.path));

  // Escape closes the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const active = session?.memberships.find(
    (m) => m.mahallu_id === session.active_mahallu_id,
  );
  const locale: Locale = i18n.language.startsWith("ml") ? "ml" : "en";
  const canSwitch = (session?.memberships.length ?? 0) > 1;

  return (
    // h-dvh + overflow-hidden, not min-h-dvh: with min-h the shell grows past
    // the viewport and the whole document scrolls, taking the sidebar and the
    // topbar with it. Exactly one scroll container in this layout -- <main>.
    <div className="flex h-dvh overflow-hidden bg-canvas text-ink">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-70 focus:rounded-sm focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-ink"
      >
        Skip to content
      </a>

      {/* The deep-green rail (approved concept): glossy gradient ground,
          white inks, a lighter-green pill for the active item, and the
          mosque silhouette fading in at the foot. */}
      {/* Scrim. Only below `lg`, where the rail floats over the content. */}
      {drawerOpen ? (
        <div
          className="fixed inset-0 z-40 bg-overlay backdrop-blur-[2px] lg:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      <aside
        {...(drawerOpen ? { role: "dialog", "aria-modal": true } : {})}
        aria-label={t("nav.community")}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-sidebar shrink-0 flex-col bg-sidebar-gloss",
          "transition-transform duration-200 ease-out",
          // From `lg` up it is a column in the layout again, always on screen.
          "lg:static lg:z-auto lg:translate-x-0 lg:transition-[width]",
          drawerOpen ? "translate-x-0 shadow-xl" : "-translate-x-full",
          collapsed ? "lg:w-sidebar-collapsed" : "lg:w-sidebar",
        )}
      >
        {/* Identity block: the code chip is how staff refer to the mahallu, and
            it is the prefix on every register number they read all day. */}
        {/* h-topbar, matching the topbar exactly: the two bottom borders are
            read as one line across the top of the app, and a few pixels of
            mismatch shows up as a broken seam. */}
        <div className="relative flex h-topbar shrink-0 items-center border-b border-sidebar-line px-3">
          <button
            onClick={() =>
              canSwitch && !collapsed && setSwitcherOpen((o) => !o)
            }
            disabled={!canSwitch || collapsed}
            aria-expanded={canSwitch && !collapsed ? switcherOpen : undefined}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-lg px-2 py-1 text-left transition-colors",
              canSwitch && !collapsed && "hover:bg-sidebar-hover",
              collapsed && "justify-center px-0",
            )}
          >
            <span className="tabular grid size-9 shrink-0 place-items-center rounded-lg bg-white text-xs font-semibold text-sidebar-chip-ink">
              {active?.mahallu_code ?? "—"}
            </span>
            {!collapsed ? (
              <span className="min-w-0 flex-1">
                <span
                  className="block truncate text-sm font-semibold text-sidebar-ink"
                  title={active?.mahallu_name_en ?? t("app.name")}
                >
                  {locale === "ml" && active?.mahallu_name_ml
                    ? active.mahallu_name_ml
                    : (active?.mahallu_name_en ?? t("app.name"))}
                </span>
                <span className="block truncate text-xs text-sidebar-ink-subtle">
                  {t("app.sub")}
                </span>
              </span>
            ) : null}
            {canSwitch && !collapsed ? (
              <ChevronsUpDown
                className="size-3.5 shrink-0 text-sidebar-ink-subtle"
                aria-hidden="true"
              />
            ) : null}
          </button>

          {switcherOpen && canSwitch ? (
            <ul
              role="menu"
              className="absolute top-topbar right-2 left-2 z-30 overflow-hidden rounded-md border border-line bg-surface shadow-md"
            >
              {session?.memberships.map((membership) => (
                <li key={membership.mahallu_id}>
                  <button
                    role="menuitem"
                    onClick={() => {
                      setSwitcherOpen(false);
                      void switchMahallu(membership.mahallu_id);
                    }}
                    className="flex w-full items-center gap-2 px-2.5 py-2 text-left text-sm hover:bg-surface-hover"
                  >
                    <span className="tabular text-2xs text-ink-subtle">
                      {membership.mahallu_code}
                    </span>
                    <span className="flex-1 truncate">
                      {membership.mahallu_name_en}
                    </span>
                    {membership.mahallu_id === session.active_mahallu_id ? (
                      <Check
                        className="size-3.5 text-primary"
                        aria-hidden="true"
                      />
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {/* Larger and calmer than it was: the office is run by elderly
            committee members, and 14px rows packed to fit one viewport were
            hard to read and harder to hit. On a short window the list now
            scrolls -- with a visible (thin) scrollbar, because a hidden one
            means the items below the fold simply do not exist for someone who
            does not think to scroll a sidebar. */}
        <nav
          className="sidebar-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto px-3 pt-3 pb-10 [@media(max-height:1100px)]:pb-3"
          aria-label="Main"
        >
          {nav.map((group) => {
            const visible = group.items.filter(
              (item) =>
                (!item.needs || can(...item.needs)) &&
                (item.needsAll ?? []).every((need) => can(need)),
            );
            if (visible.length === 0) return null;
            return (
              <div key={group.id}>
                {!collapsed ? (
                  <p className="px-3 pb-2 text-xs leading-none font-medium tracking-wide text-sidebar-ink-subtle uppercase">
                    {t(group.labelKey)}
                  </p>
                ) : null}
                <ul className="space-y-0.5">
                  {visible.map((item) => {
                    const isActive = isCurrent(
                      currentPath,
                      item.path,
                      allPaths,
                    );
                    return (
                      <li key={item.id}>
                        <button
                          onClick={() => {
                            // Tapping a destination should reveal it, not
                            // leave the drawer covering the screen you asked
                            // for. No-op above `lg`, where it is never open.
                            setDrawerOpen(false);
                            onNavigate(item.path);
                          }}
                          aria-current={isActive ? "page" : undefined}
                          title={collapsed ? t(item.labelKey) : undefined}
                          className={cn(
                            // Lighter-green pill on the dark rail (approved
                            // concept) — lit from within, not washed white.
                            // A 40px row at 17px: big enough to read at arm's
                            // length and to hit without aiming.
                            "group flex min-h-10 w-full items-center gap-3 rounded-md px-3 py-2 text-base leading-tight font-medium transition-colors duration-[80ms]",
                            isActive
                              ? "bg-sidebar-pill text-white shadow-button"
                              : "text-sidebar-ink-muted hover:bg-sidebar-hover hover:text-sidebar-ink",
                            collapsed && "justify-center px-0",
                          )}
                        >
                          <item.icon
                            className="size-5 shrink-0"
                            aria-hidden="true"
                            strokeWidth={1.75}
                          />
                          {!collapsed ? (
                            <span className="flex-1 text-left">
                              {t(item.labelKey)}
                            </span>
                          ) : null}
                          {/* Counts turn the nav into an at-a-glance summary of
                              the community, which is what staff want to know. */}
                          {!collapsed && item.count !== undefined ? (
                            <span
                              className={cn(
                                "tabular text-xs",
                                isActive
                                  ? // Full white, not white/80: at 12px WCAG
                                    // wants 4.5:1, and the softened white is
                                    // 3.55:1 against the pill where white is
                                    // 4.61:1. The count stays subordinate to
                                    // the label by size, which costs nothing.
                                    "text-white"
                                  : "text-sidebar-ink-subtle",
                              )}
                            >
                              {formatCount(item.count, locale)}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* The rail's foot: the mosque silhouette alone. Absolutely
            positioned, so it takes no space in the flex column and can never
            be the reason the nav scrolls.

            The asset carries its own alpha and is cropped to the exact bounds
            of the artwork -- the earlier crop kept the source's transparent
            padding, which is what held the hills off the bottom edge. No blend
            mode: brightness(0) recolours every opaque pixel to black and
            invert(1) flips that to white, so the shape stays and only its
            colour changes. */}
        {!collapsed ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 [@media(max-height:1100px)]:hidden">
            <img
              src="/sidebar-masjid.webp"
              alt=""
              className="block w-full opacity-40 [filter:brightness(0)_invert(1)]"
            />
          </div>
        ) : null}

        {/* Collapse toggle: a white disc straddling the rail's edge, centred
            vertically — the one element that visibly floats (shadow-float). */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          aria-label={t(
            collapsed ? "nav.expandSidebar" : "nav.collapseSidebar",
          )}
          aria-expanded={!collapsed}
          className="absolute top-1/2 -right-3 z-30 hidden size-6 -translate-y-1/2 place-items-center rounded-full bg-surface text-ink-muted shadow-float transition-colors hover:text-ink lg:grid"
        >
          <ChevronLeft
            className={cn(
              "size-3.5 transition-transform duration-200",
              collapsed && "rotate-180",
            )}
            aria-hidden="true"
          />
        </button>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* Border swapped for a cast shadow (z-10 so it paints over the page
            content scrolling beneath, not under it). */}
        <header className="relative z-10 flex h-topbar shrink-0 items-center justify-end gap-2 bg-surface px-3 shadow-topbar sm:px-4">
          {/* The only way to the navigation below `lg`. Above it the rail is
              always on screen, so the control would be a button to nothing. */}
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label={t("nav.community")}
            aria-expanded={drawerOpen}
            // mr-auto rather than justify-between on the header: above `lg`
            // this button is display:none, and a lone remaining child under
            // justify-between sits at flex-start -- which is how the language
            // toggle and the account block ended up on the left.
            className="grid size-11 shrink-0 place-items-center rounded-md text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink lg:hidden"
          >
            <Menu className="size-6" aria-hidden="true" />
          </button>

          {/* Pushes the language and account controls to the right. */}
          <div className="mr-auto" />

          <div className="flex min-w-0 items-center gap-1.5">
            {/* Two languages, so a toggle beats a dropdown: one click, no menu. */}
            <button
              onClick={() => setLocale(locale === "en" ? "ml" : "en")}
              className="flex h-11 items-center gap-2 rounded-md px-3 text-base font-medium text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
            >
              <Languages className="size-5" aria-hidden="true" />
              {locale === "en" ? t("language.ml") : t("language.en")}
            </button>
            {/* Identity, not a control: who is signed in. No chevron and
                nothing to click -- Settings and Sign out both live in the
                nav, and a disclosure arrow that went straight to Settings
                only promised a menu that was never there. */}
            <div className="flex items-center gap-2.5 px-2 py-1">
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-ink">
                {initials(session?.user.full_name)}
              </span>
              <span className="hidden leading-tight sm:block">
                <span className="block max-w-48 truncate text-base font-semibold">
                  {session?.user.full_name}
                </span>
                <span className="block text-xs text-ink-muted">
                  {session?.roles?.[0] ? t(`role.${session.roles[0]}`) : null}
                </span>
              </span>
            </div>
          </div>
        </header>

        {/* Does not scroll. Each page owns its scroll region via <Page>, so
            the page title stays put and only the content moves. */}
        <main id="main" className="min-h-0 flex-1 overflow-hidden">
          {/* Full-bleed: pages own their 24px gutters. The old max-w-content
              cap centred a 1440px column, which on wide monitors read as huge
              dead margins either side of every table. */}
          <div className="h-full w-full">{children}</div>
        </main>
      </div>
    </div>
  );
}
